import React, { useState } from 'react';
import { Eye, Target, Quote, Lightbulb, Users, TrendingUp, Award, CheckCircle, Zap, Shield } from 'lucide-react';

const VisionMission = () => {
  const [activeTab, setActiveTab] = useState('vision');

  const visionPoints = [
    {
      id: 1,
      icon: TrendingUp,
      title: 'Industrial Excellence',
      description: 'Transform Vidarbha into a vibrant industrial powerhouse with sustainable growth',
      color: '#3B82F6'
    },
    {
      id: 2,
      icon: Users,
      title: 'Collaborative Ecosystem',
      description: 'Foster collaboration among industry leaders, entrepreneurs, and stakeholders',
      color: '#10B981'
    },
    {
      id: 3,
      icon: Lightbulb,
      title: 'Innovation Hub',
      description: 'Convert bold ideas into sustainable, long-term growth opportunities',
      color: '#F59E0B'
    },
    {
      id: 4,
      icon: Award,
      title: 'Future-Ready Region',
      description: 'Position Vidarbha as a model for integrated industrial development',
      color: '#8B5CF6'
    }
  ];

  const missionPoints = [
    {
      id: 1,
      icon: Zap,
      title: 'Enable Investment',
      description: 'Attract and facilitate strategic investments across multiple sectors',
      color: '#3B82F6'
    },
    {
      id: 2,
      icon: Shield,
      title: 'Build Infrastructure',
      description: 'Develop world-class infrastructure to support industrial growth',
      color: '#10B981'
    },
    {
      id: 3,
      icon: Users,
      title: 'Empower Talent',
      description: 'Create skilled workforce through comprehensive training programs',
      color: '#F59E0B'
    },
    {
      id: 4,
      icon: CheckCircle,
      title: 'Unite Stakeholders',
      description: 'Bring together industry, government, and youth for collective progress',
      color: '#8B5CF6'
    }
  ];

  const coreValues = [
    { title: 'Integrity', description: 'Upholding ethical standards in all our endeavors' },
    { title: 'Innovation', description: 'Embracing creativity and forward-thinking solutions' },
    { title: 'Collaboration', description: 'Building strong partnerships for shared success' },
    { title: 'Excellence', description: 'Striving for the highest quality in everything we do' },
    { title: 'Sustainability', description: 'Ensuring long-term viability and environmental responsibility' },
    { title: 'Empowerment', description: 'Enabling communities and individuals to thrive' }
  ];

  return (
    <div style={{ fontFamily: '"Rubik", sans-serif', backgroundColor: "#fff", minHeight: "100vh", color: "#333", marginBottom: "60px" }}>
      <style>{`
        @media (max-width: 768px) {
          .vm-hero-section {
            padding: 32px 20px 24px !important;
          }
          .vm-hero-badge {
            margin-bottom: 16px !important;
            padding: 8px 18px !important;
          }
          .vm-hero-title {
            font-size: 30px !important;
            margin-bottom: 10px !important;
          }
          .vm-hero-desc {
            font-size: 15px !important;
            margin: 0 auto 20px !important;
          }
          .vm-hero-tabs {
            margin-top: 8px !important;
          }
          .vm-hero-tab-btn {
            padding: 10px 24px !important;
            font-size: 14px !important;
          }
          .vm-section-title {
            font-size: 28px !important;
          }
          .vm-section-desc {
            font-size: 15px !important;
          }
          .vm-statement-section {
            padding: 24px 20px 20px !important;
          }
        }
      `}</style>

      {/* HERO SECTION */}
      <div className="vm-hero-section" style={{
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, #fafbfc 0%, #f0f0f5 100%)",
        padding: "50px 20px 40px"
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          {/* Badge */}
          <div className="vm-hero-badge" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 24px",
            background: "linear-gradient(135deg, #470172 0%, #764ba2 100%)",
            borderRadius: "50px",
            marginBottom: "18px",
            boxShadow: "0 4px 20px rgba(71, 1, 114, 0.25)"
          }}>
            <Eye size={20} color="#fff" />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#fff", letterSpacing: "0.5px" }}>
              Corporate Philosophy
            </span>
          </div>

          {/* Main Title */}
          <h1 className="vm-hero-title" style={{
            fontSize: "56px",
            fontWeight: "700",
            lineHeight: "1.2",
            marginBottom: "12px",
            letterSpacing: "-1px"
          }}>
            Vision &{' '}
            <span style={{ 
              background: "linear-gradient(135deg, #470172 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text"
            }}>
              Mission
            </span>
          </h1>

          {/* Description */}
          <p className="vm-hero-desc" style={{
            fontSize: "18px",
            color: "#4a5568",
            lineHeight: "1.8",
            maxWidth: "750px",
            margin: "0 auto 24px"
          }}>
            Guiding principles that drive our commitment to transforming Vidarbha into India's next industrial powerhouse
          </p>

          {/* Tab Navigation */}
          <div className="vm-hero-tabs" style={{
            display: "inline-flex",
            background: "#fff",
            padding: "6px",
            borderRadius: "50px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            marginTop: "0"
          }}>
            <button
              className="vm-hero-tab-btn"
              onClick={() => setActiveTab('vision')}
              style={{
                padding: "12px 40px",
                borderRadius: "50px",
                border: "none",
                background: activeTab === 'vision'
                  ? "linear-gradient(135deg, #470172 0%, #764ba2 100%)"
                  : "transparent",
                color: activeTab === 'vision' ? "#fff" : "#4a5568",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
            >
              Our Vision
            </button>
            <button
              className="vm-hero-tab-btn"
              onClick={() => setActiveTab('mission')}
              style={{
                padding: "12px 40px",
                borderRadius: "50px",
                border: "none",
                background: activeTab === 'mission'
                  ? "linear-gradient(135deg, #470172 0%, #764ba2 100%)"
                  : "transparent",
                color: activeTab === 'mission' ? "#fff" : "#4a5568",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
            >
              Our Mission
            </button>
          </div>
        </div>
      </div>

      {/* VISION SECTION */}
      {activeTab === 'vision' && (
        <>
          {/* Vision Statement Card */}
          <div className="vm-statement-section" style={{ padding: "30px 20px 20px", backgroundColor: "#fff" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
              <div style={{
                background: "#fff",
                borderRadius: "20px",
                padding: "40px 35px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
                border: "2px solid #f0f0f0"
              }}>
                {/* Background Pattern */}
                <div style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  opacity: 0.05
                }}>
                  {/* <Eye size={200} color="#470172" /> */}
                </div>

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "20px"
                  }}>
                    <div style={{
                      width: "50px",
                      height: "50px",
                      background: "linear-gradient(135deg, #470172 0%, #764ba2 100%)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)"
                    }}>
                      <Eye size={24} color="#fff" />
                    </div>
                    <h2 style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#1a202c",
                      margin: 0
                    }}>
                      Our Vision
                    </h2>
                  </div>

                  <p style={{
                    fontSize: "17px",
                    color: "#4a5568",
                    lineHeight: "1.7",
                    marginBottom: "20px",
                    fontWeight: "400"
                  }}>
                    AID aims to transform Vidarbha into a vibrant, future-ready industrial powerhouse by fostering collaboration among industry leaders, entrepreneurs, and stakeholders. We envision an ecosystem that converts bold ideas into sustainable, long-term growth opportunities across sectors.
                  </p>

                  {/* Quote Box */}
                  <div style={{
                    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
                    borderLeft: "3px solid #470172",
                    borderRadius: "0 10px 10px 0",
                    padding: "18px 22px",
                    backdropFilter: "blur(10px)",
                    position: "relative"
                  }}>
                    <Quote size={20} color="#470172" style={{ opacity: 0.5, marginBottom: "8px" }} />
                    <p style={{
                      fontSize: "15px",
                      color: "#4a5568",
                      fontStyle: "italic",
                      lineHeight: "1.6",
                      margin: 0
                    }}>
                      "We envision Vidarbha emerging as a thriving centre of industrial excellence—where innovation flourishes, youth are empowered, and collaboration drives transformative progress."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vision Points Grid */}
          <div style={{ padding: "40px 20px 80px", backgroundColor: "#fafbfc" }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <h2 className="vm-section-title" style={{
                  fontSize: "42px",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "16px",
                  letterSpacing: "-0.5px"
                }}>
                  Strategic Pillars of Our Vision
                </h2>
                <p className="vm-section-desc" style={{ fontSize: "17px", color: "#4a5568", maxWidth: "650px", margin: "0 auto" }}>
                  Key focus areas that define our vision for Vidarbha's transformation
                </p>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "25px"
              }}>
                {visionPoints.map((point) => {
                  const Icon = point.icon;
                  return (
                    <div
                      key={point.id}
                      style={{
                        background: "#fff",
                        borderRadius: "20px",
                        padding: "35px 30px",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                        border: "2px solid #f0f0f0",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 15px 40px rgba(71, 1, 114, 0.15)";
                        e.currentTarget.style.borderColor = point.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.06)";
                        e.currentTarget.style.borderColor = "#f0f0f0";
                      }}
                    >
                      <div style={{
                        width: "65px",
                        height: "65px",
                        background: `${point.color}15`,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px"
                      }}>
                        <Icon size={32} color={point.color} strokeWidth={2.5} />
                      </div>

                      <h3 style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1a202c",
                        marginBottom: "12px"
                      }}>
                        {point.title}
                      </h3>

                      <p style={{
                        fontSize: "15px",
                        color: "#4a5568",
                        lineHeight: "1.7",
                        margin: 0
                      }}>
                        {point.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* MISSION SECTION */}
      {activeTab === 'mission' && (
        <>
          {/* Mission Statement Card */}
          <div className="vm-statement-section" style={{ padding: "30px 20px 20px", backgroundColor: "#fff" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
              <div style={{
                background: "#fff",
                borderRadius: "20px",
                padding: "40px 35px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
                border: "2px solid #f0f0f0"
              }}>
                {/* Background Pattern */}
                <div style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  opacity: 0.05
                }}>
                  {/* <Target size={200} color="#10B981" /> */}
                </div>

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "20px"
                  }}>
                    <div style={{
                      width: "50px",
                      height: "50px",
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)"
                    }}>
                      <Target size={24} color="#fff" />
                    </div>
                    <h2 style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#1a202c",
                      margin: 0
                    }}>
                      Our Mission
                    </h2>
                  </div>

                  <p style={{
                    fontSize: "17px",
                    color: "#4a5568",
                    lineHeight: "1.7",
                    marginBottom: "20px",
                    fontWeight: "400"
                  }}>
                    AID's mission is to drive Vidarbha's rise as India's next industrial powerhouse by enabling investment, innovation, and skilled talent. We work to unite industry, government, and youth to build world-class infrastructure and create sustainable opportunities.
                  </p>

                  {/* Quote Box */}
                  <div style={{
                    background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                    borderLeft: "3px solid #10B981",
                    borderRadius: "0 10px 10px 0",
                    padding: "18px 22px",
                    backdropFilter: "blur(10px)",
                    position: "relative"
                  }}>
                    <Quote size={20} color="#10B981" style={{ opacity: 0.5, marginBottom: "8px" }} />
                    <p style={{
                      fontSize: "15px",
                      color: "#4a5568",
                      fontStyle: "italic",
                      lineHeight: "1.6",
                      margin: 0
                    }}>
                      "AID is committed to transforming Vidarbha into a high-growth industrial ecosystem by promoting strategic investments and fostering multi-sector development."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission Points Grid */}
          <div style={{ padding: "40px 20px 80px", backgroundColor: "#fafbfc" }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: "60px" }}>
                <h2 className="vm-section-title" style={{
                  fontSize: "42px",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "16px",
                  letterSpacing: "-0.5px"
                }}>
                  Core Mission Objectives
                </h2>
                <p className="vm-section-desc" style={{ fontSize: "17px", color: "#4a5568", maxWidth: "650px", margin: "0 auto" }}>
                  Strategic initiatives to achieve our mission of industrial transformation
                </p>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "25px"
              }}>
                {missionPoints.map((point) => {
                  const Icon = point.icon;
                  return (
                    <div
                      key={point.id}
                      style={{
                        background: "#fff",
                        borderRadius: "20px",
                        padding: "35px 30px",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                        border: "2px solid #f0f0f0",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 15px 40px rgba(16, 185, 129, 0.15)";
                        e.currentTarget.style.borderColor = point.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.06)";
                        e.currentTarget.style.borderColor = "#f0f0f0";
                      }}
                    >
                      <div style={{
                        width: "65px",
                        height: "65px",
                        background: `${point.color}15`,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px"
                      }}>
                        <Icon size={32} color={point.color} strokeWidth={2.5} />
                      </div>

                      <h3 style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1a202c",
                        marginBottom: "12px"
                      }}>
                        {point.title}
                      </h3>

                      <p style={{
                        fontSize: "15px",
                        color: "#4a5568",
                        lineHeight: "1.7",
                        margin: 0
                      }}>
                        {point.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* CORE VALUES SECTION */}
      {/* <div style={{ padding: "80px 20px", backgroundColor: "#fff" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <h2 style={{ 
              fontSize: "46px", 
              fontWeight: "700", 
              color: "#1a202c", 
              marginBottom: "20px",
              letterSpacing: "-0.5px"
            }}>
              Our Core Values
            </h2>
            <p style={{ fontSize: "18px", color: "#4a5568", maxWidth: "700px", margin: "0 auto", lineHeight: "1.7" }}>
              Fundamental principles that guide our actions and shape our organizational culture
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px"
          }}>
            {coreValues.map((value, index) => (
              <div
                key={index}
                style={{
                  background: "linear-gradient(135deg, #fafbfc 0%, #f0f0f5 100%)",
                  borderRadius: "16px",
                  padding: "30px 25px",
                  border: "2px solid #e5e7eb",
                  transition: "all 0.3s ease",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #470172 0%, #764ba2 100%)";
                  e.currentTarget.style.borderColor = "#470172";
                  e.currentTarget.style.transform = "translateY(-3px)";
                  const title = e.currentTarget.querySelector('h3');
                  const desc = e.currentTarget.querySelector('p');
                  if (title) title.style.color = "#fff";
                  if (desc) desc.style.color = "rgba(255,255,255,0.9)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #fafbfc 0%, #f0f0f5 100%)";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "translateY(0)";
                  const title = e.currentTarget.querySelector('h3');
                  const desc = e.currentTarget.querySelector('p');
                  if (title) title.style.color = "#1a202c";
                  if (desc) desc.style.color = "#4a5568";
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "12px"
                }}>
                  <CheckCircle size={22} color="#470172" strokeWidth={2.5} />
                  <h3 style={{
                    fontSize: "19px",
                    fontWeight: "700",
                    color: "#1a202c",
                    margin: 0,
                    transition: "color 0.3s ease"
                  }}>
                    {value.title}
                  </h3>
                </div>
                <p style={{
                  fontSize: "14px",
                  color: "#4a5568",
                  lineHeight: "1.6",
                  margin: 0,
                  transition: "color 0.3s ease"
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div> */}

    </div>
  );
};

export default VisionMission;
